import { ReactNode, useState } from "react";
import { Check, CaretDown } from "@phosphor-icons/react";
import { cn } from "../../../lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger
} from "../../shadcn";
import { AppButton } from "./app-button";
import { InputLabel } from "./input-label";

interface SelectionDropdownProps {
  options: { value: string; label: string; component?: ReactNode }[];
  placeholder?: string;
  value: string | undefined;
  onChange: (value: string) => void;
  className?: string;
  error?: string;
  disabled?: boolean;
  label?: string;
  isOptional?: boolean;
  modal?: boolean;
}

export function SelectionDropdown({
  options,
  placeholder = "Select option...",
  value,
  onChange,
  className,
  error,
  disabled,
  label,
  isOptional,
  modal
}: SelectionDropdownProps) {
  const [open, setOpen] = useState(false);

  const selectedOption = options.find((option) => option.value === value);

  return (
    <div className="flex flex-col relative">
      {label && (
        <InputLabel isOptional={isOptional} className="mb-[9px]">
          {label}
        </InputLabel>
      )}
      <Popover open={open} onOpenChange={setOpen} modal={modal}>
        <PopoverTrigger asChild disabled={disabled}>
          <AppButton
            appearance="outlined"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between font-normal",
              value ? "text-zinc-900" : "text-innoveraZinc-400 hover:text-innoveraZinc-400",
              !!error && "border-innoveraRed-400 hover:ring-innoveraRed-50 focus-visible:ring-innoveraRed-50",
              className
            )}
            size="default"
            disabled={disabled}
          >
            <span className="truncate">{selectedOption?.component || selectedOption?.label || placeholder}</span>
            <CaretDown className="opacity-50 shrink-0 ml-2" />
          </AppButton>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-auto min-w-(--radix-popover-trigger-width) max-w-[400px] p-0"
          sideOffset={4}
        >
          <Command>
            <CommandInput placeholder="Search option..." className="h-9" />
            <CommandList>
              <CommandEmpty>No option found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={(currentValue) => {
                      onChange(currentValue === value ? "" : currentValue);
                      setOpen(false);
                    }}
                    className="min-w-0"
                  >
                    <span className="flex-1 truncate">{option.component || option.label}</span>
                    <Check
                      className={cn("ml-auto shrink-0", value === option.value ? "opacity-100" : "opacity-0")}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {error && <p className="absolute bottom-[-14px] left-1 text-innoveraRed-400 text-[10px]">{error}</p>}
    </div>
  );
}
